import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { getImagePath } from '../config/siteConfig';
import { usePackages } from '../context/PackageContext';
import '../styles/PopularDestinations.css';

const PopularDestinations = () => {
  const { packages } = usePackages();
  const [selectedDocs, setSelectedDocs] = useState(null);
  const [showDocModal, setShowDocModal] = useState(false);

  const destinations = [
    {
      id: 1,
      name: 'Istanbul',
      country: 'Turkey',
      image: getImagePath('/assets/images/trips/istanbul/day1-1.png'),
      description: '5 Days / 4 Nights',
      link: '/trip-details/istanbul-5d4n',
      packageId: 7
    },
    {
      id: 2,
      name: 'Tokyo',
      country: 'Japan',
      image: getImagePath('/assets/images/trips/japan/day1-1.png'),
      description: '6 Days / 5 Nights',
      link: '/trip-details/japan-6d5n',
      packageId: 1
    },
    {
      id: 3,
      name: 'Europe',
      country: 'Multi-City Tour',
      image: getImagePath('/assets/images/trips/europe/day1-1.png'),
      description: '10 Days / 9 Nights',
      link: '/trip-details/europe-10d9n',
      packageId: 8
    },
    {
      id: 4,
      name: 'Morocco',
      country: 'Africa',
      image: getImagePath('/assets/images/trips/morocco/day1-1.png'),
      description: '7 Days / 6 Nights',
      link: '/trip-details/morocco-7d6n',
      packageId: 12
    },
    {
      id: 5,
      name: 'Kenya',
      country: 'Safari Adventure',
      image: getImagePath('/assets/images/trips/kenya/day1-1.png'),
      description: '8 Days / 7 Nights',
      link: '/trip-details/kenya-8d7n',
      packageId: 13
    },
    {
      id: 6,
      name: 'Vietnam',
      country: 'Southeast Asia',
      image: getImagePath('/assets/images/trips/vietnam/option-1-day4-2.png'),
      description: '6 Days / 5 Nights',
      link: '/trip-details/vietnam-6d5n',
      packageId: 4
    }
  ];

  const handleDestinationClick = (e, dest) => {
    const packageData = packages.find(p => p.id === dest.packageId);
    if (packageData && packageData.documents && packageData.documents.length > 0) {
      e.preventDefault();
      setSelectedDocs({
        title: packageData.title,
        documents: packageData.documents,
        link: packageData.link
      });
      setShowDocModal(true);
    }
  };

  const closeModal = () => {
    setShowDocModal(false);
    setSelectedDocs(null);
  };

  const handleDocumentAction = (doc) => {
    // Download all document types - both PDF and DOCX with proper blob handling
    const pdfPath = getImagePath(`/assets/pdfs/${doc.name}`);
    
    fetch(pdfPath)
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.blob();
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = doc.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(error => {
        console.error('Download error:', error);
        alert('Failed to download the file. Please try again.');
      });
  };

  return (
    <section className="popular-destinations-modern">
      <div className="destinations-container">
        <div className="section-header-modern">
          <div className="header-content">
            <span className="section-badge">EXPLORE THE WORLD</span>
            <h2 className="section-title-modern">Popular Destinations</h2>
            <p className="section-subtitle-modern">
              Handpicked destinations that promise unforgettable experiences
            </p>
          </div>
        </div>
        
        <div className="destinations-gallery">
          {destinations.map((dest) => {
            const hasDocuments = packages.find(p => p.id === dest.packageId)?.documents?.length > 0;
            
            return (
              <div 
                key={dest.id}
                onClick={(e) => hasDocuments && handleDestinationClick(e, dest)}
                className="gallery-item-wrapper"
              >
                <Link 
                  to={dest.link}
                  className="gallery-item"
                  onClick={(e) => hasDocuments && e.preventDefault()}
                >
                  <div className="gallery-image">
                    <img src={dest.image} alt={dest.name} />
                    <div className="gallery-overlay">
                      <div className="overlay-content">
                        <div className="destination-info">
                          <h3 className="destination-title">{dest.name}</h3>
                          <p className="destination-location">
                            <i className="fas fa-map-marker-alt"></i>
                            {dest.country}
                          </p>
                        </div>
                        <div className="destination-meta">
                          <span className="duration">
                            <i className="fas fa-clock"></i>
                            {dest.description}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
                {hasDocuments && (
                  <div className="doc-badge">
                    <i className="fas fa-file-pdf"></i>
                    <span>View Docs</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Documents Modal */}
      {showDocModal && selectedDocs && (
        <div className="doc-modal-overlay" onClick={closeModal}>
          <div className="doc-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="doc-modal-close" onClick={closeModal}>
              <i className="fas fa-times"></i>
            </button>
            
            <div className="doc-modal-header">
              <h2>{selectedDocs.title}</h2>
              <p>Download Itinerary & Documents</p>
            </div>

            <div className="doc-modal-body">
              {selectedDocs.documents.map((doc, index) => (
                <div key={index} className="doc-modal-item">
                  <div className="doc-modal-icon">
                    <i className={doc.type === 'pdf' ? 'fas fa-file-pdf' : 'fas fa-file-word'}></i>
                  </div>
                  <div className="doc-modal-info">
                    <h4>{doc.description || doc.name}</h4>
                    <p>{doc.name}</p>
                  </div>
                  <div className="doc-modal-actions">
                    <button 
                      onClick={() => handleDocumentAction(doc)}
                      className="btn-doc-modal download"
                    >
                      <i className="fas fa-download"></i> Download
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="doc-modal-footer">
              <Link to={selectedDocs.link} className="btn-view-full" onClick={closeModal}>
                <i className="fas fa-arrow-right"></i> View Full Details
              </Link>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PopularDestinations;
